import { useState } from 'react';
import { noDataCopy } from '../../../../../constants/constants';
import { REFERENCE_MODEL_TYPE } from '../../../../../types/global';
import { IToolData } from '../../../../../types/products';
import { ICountryDetail } from '../../../../../types/countries';
import { formatLocationName } from '../../../../LogisticServices/utils/general.utils';
import { QRDownloadButton } from '../../../../../components';
import { QRButtonSize } from '../../../../../components/QRDownloadButton/QRDownloadButton';
import NudosProductPills from '../../../../../components/NudosComponents/NudosProductPills/NudosProductPills';
import { IpillsByReference } from '../../../../../types/orders';
import AssetLocationDisplay from '../../../../../components/AssetLocationDisplay/AssetLocationDisplay';
import { LOCATION_PLACE_TYPE } from '../../../../../types/services';
import { LIST_PRODUCTS_TYPE } from '../../../../../types/orders';
import { NudosHoverText } from 'components/NudosComponents';
import BordOneToneIcon from '../../../../../components/NudosComponents/BordOneToneIcon/BordOneToneIcon';
import { displaySuccessNotification } from '../../../../../utils/displayNudosStandardNotifications';
import { copyClipboard } from 'utils/copyClipboard';
import { bordColorTheme, nudosTextcolor } from 'styles/styles.constants';
import './ProductModuleListingCard.scss';

/**
 * Componente para la carta de producto en la lista de productos
 */
const ProductModuleListingCard = ({
  product,
  listOfCountries,
  handleModalSide,
  listProductsType
}: IProductModuleListingCardProps) => {
  const [isHoveringSerial, setIsHoveringSerial] = useState(false);

  const {
    id,
    serial,
    organizationName,
    sku,
    name,
    pills,
    referenceModel,
    productCondition,
    location,
    inTransit,
    employeeName,
    stockOrderStatus
  } = product;

  const getReferenceModelText = () => {
    switch (referenceModel) {
      case REFERENCE_MODEL_TYPE.INTERNAL:
        return 'Herramienta Bord';
      case REFERENCE_MODEL_TYPE.EXTERNAL:
        return 'Herramienta externa';
      case REFERENCE_MODEL_TYPE.STOCK:
        return 'Herramienta Stock propio';
      default:
        return 'Tipo no especificado';
    }
  };

  const handleCopySerial = (e: React.MouseEvent) => {
    e?.stopPropagation();
    if (!serial) return;

    copyClipboard({ text: serial });
    displaySuccessNotification({
      customJSXMessage: <>Serial copiado con éxito</>,
      duration: 3000
    });
  };

  const handleSerialMouseEnter = () => {
    setIsHoveringSerial(true);
  };

  const handleSerialMouseLeave = () => {
    setIsHoveringSerial(false);
  };

  const formattedPills = typeof pills === 'string' ? (JSON.parse(pills) as IpillsByReference) : pills || {};

  const referenceModelText = getReferenceModelText();

  const qrData = {
    serial: serial,
    orgName: organizationName,
    productId: id
  };

  const disableQrDownload = ![
    LOCATION_PLACE_TYPE.NUDOS,
    LOCATION_PLACE_TYPE.DIAGNOSTIC,
    LOCATION_PLACE_TYPE.WARRANTY
  ].includes(location?.place as LOCATION_PLACE_TYPE);

  const handleQrButtonClickPropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleClick = () => {
    if (!id) return;

    handleModalSide(id);
  };

  const isStockBord = listProductsType === LIST_PRODUCTS_TYPE.STOCK_BORD;

  return (
    <div
      className={`productModuleListingCard withHover ${isStockBord ? 'stockBord' : ''}`}
      onClick={handleClick}
    >
      <div
        className="tableField serial serialWrapper"
        onMouseEnter={handleSerialMouseEnter}
        onMouseLeave={handleSerialMouseLeave}
        onClick={serial ? handleCopySerial : undefined}
      >
        <NudosHoverText text={serial || noDataCopy} charactersLimit={10} onlyIfTruncated />
        {isHoveringSerial && serial && (
          <div className="copyIconContainer">
            <BordOneToneIcon variant="copy" standardSize={10} stroke={bordColorTheme.navyBlue[900]} />
          </div>
        )}
      </div>
      <div className="tableField cliente">{organizationName || noDataCopy}</div>
      <div className="tableField sku">{sku || noDataCopy}</div>
      <div className="tableField name">{name || noDataCopy}</div>
      <div className="tableField pills">
        <NudosProductPills pills={formattedPills} characterLimit={6} />
      </div>
      <div className="tableField condition">{productCondition || noDataCopy}</div>
      {inTransit && !!inTransit.origin && !!inTransit.destination && (
        <div className="tableField inTransitLocations">
          <AssetLocationDisplay
            location={inTransit?.origin}
            locationName={formatLocationName(inTransit?.origin, listOfCountries, inTransit?.origin?.locationName)}
          />
          <div className="w-full">
            <BordOneToneIcon variant="arrowRight" standardSize={8} stroke={nudosTextcolor} />
          </div>
          <AssetLocationDisplay
            location={inTransit?.destination}
            locationName={formatLocationName(
              inTransit?.destination,
              listOfCountries,
              inTransit?.destination?.locationName
            )}
          />
        </div>
      )}
      {(!inTransit || !inTransit?.origin || !inTransit?.destination) && (
        <div className="tableField location">
          <AssetLocationDisplay
            location={location}
            locationName={formatLocationName(location, listOfCountries, employeeName)}
          />
        </div>
      )}
      {isStockBord && <div className="tableField stockStatus">{stockOrderStatus}</div>}
      <div className="tableField referenceModel">{referenceModelText}</div>
      <div className="tableField actions" onClick={handleQrButtonClickPropagation}>
        <QRDownloadButton QRData={qrData} size={QRButtonSize.SMALL} disabled={disableQrDownload} />
      </div>
    </div>
  );
};

export default ProductModuleListingCard;

interface IProductModuleListingCardProps {
  product: IToolData;
  listOfCountries: ICountryDetail[];
  handleModalSide: (productId: number) => void;
  listProductsType: LIST_PRODUCTS_TYPE;
  isStockBord?: boolean;
}
